import * as THREE from 'three';
import { MAP_BACKUP_USERDATA_KEY } from './materialMaps';

const PREVIEW_SIZE = 96;

const TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'alphaMap',
  'aoMap',
  'bumpMap',
  'matcap',
];

let sharedRenderer = null;
let sharedScene = null;
let sharedCamera = null;
let sharedSphere = null;
let sharedBackdrop = null;

/** uuid|fingerprint -> dataURL */
const previewCache = new Map();
const MAX_CACHE = 64;

/** Serialize WebGL work so many list items don't stampede the GPU. */
let renderQueue = Promise.resolve();
let queueDepth = 0;

function ensureSharedPreviewContext() {
  if (sharedRenderer) return;

  sharedRenderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    preserveDrawingBuffer: true,
    powerPreference: 'low-power',
  });
  sharedRenderer.setPixelRatio(1);
  sharedRenderer.setSize(PREVIEW_SIZE, PREVIEW_SIZE, false);
  sharedRenderer.setClearColor(0x505050, 1);
  sharedRenderer.outputColorSpace = THREE.SRGBColorSpace;
  sharedRenderer.toneMapping = THREE.ACESFilmicToneMapping;
  sharedRenderer.toneMappingExposure = 1.25;
  sharedRenderer.shadowMap.enabled = true;
  sharedRenderer.shadowMap.type = THREE.PCFSoftShadowMap;

  sharedScene = new THREE.Scene();
  sharedScene.background = new THREE.Color(0x505050);

  sharedCamera = new THREE.PerspectiveCamera(35, 1, 0.1, 10);
  sharedCamera.position.set(0, 0.08, 2.5);
  sharedCamera.lookAt(0, 0, 0);

  const ambient = new THREE.HemisphereLight(0xeaf2ff, 0x182033, 1.15);
  const key = new THREE.DirectionalLight(0xffffff, 2.6);
  key.position.set(-2.4, 3.2, 3.2);
  key.castShadow = true;
  key.shadow.mapSize.set(256, 256);
  key.shadow.camera.left = -2;
  key.shadow.camera.right = 2;
  key.shadow.camera.top = 2;
  key.shadow.camera.bottom = -2;
  key.shadow.bias = -0.0008;

  const fill = new THREE.DirectionalLight(0x8fb7ff, 0.65);
  fill.position.set(2.5, 0.5, 2);
  sharedScene.add(ambient, key, fill);

  const geo = new THREE.SphereGeometry(0.8, 32, 24);
  sharedSphere = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({ color: 0x888888 })
  );
  sharedSphere.position.z = 0;
  sharedSphere.castShadow = true;
  sharedSphere.receiveShadow = true;

  // A studio backdrop catches the sphere shadow without changing the material.
  sharedBackdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(5, 5),
    new THREE.MeshStandardMaterial({
      color: 0x505050,
      roughness: 1,
      metalness: 0,
    })
  );
  sharedBackdrop.position.z = -0.92;
  sharedBackdrop.receiveShadow = true;

  sharedScene.add(sharedBackdrop, sharedSphere);
}

function textureId(tex) {
  if (!tex) return '0';
  return tex.uuid || tex.id || '1';
}

/** Cheap fingerprint so we only re-render when the look actually changes.
 * Uses authored (pre–Render View) props when present so Wireframe/Fade in the
 * grid does not refresh or alter Materials-in-model sphere previews.
 */
export function getMaterialPreviewFingerprint(mat) {
  if (!mat) return 'null';
  const orig = mat.userData?.originalMaterialProps;
  const color =
    orig?.color !== undefined
      ? orig.color
      : mat.color?.isColor
        ? mat.color.getHex()
        : 0;
  const emissive =
    orig?.emissive !== undefined
      ? orig.emissive
      : mat.emissive?.isColor
        ? mat.emissive.getHex()
        : 0;
  const opacity =
    orig?.opacity !== undefined
      ? orig.opacity
      : typeof mat.opacity === 'number'
        ? mat.opacity
        : 1;
  const transparent =
    orig?.transparent !== undefined ? !!orig.transparent : !!mat.transparent;
  const alphaTest =
    orig?.alphaTest !== undefined
      ? orig.alphaTest
      : typeof mat.alphaTest === 'number'
        ? mat.alphaTest
        : 0;
  const wireframe =
    orig?.wireframe !== undefined ? !!orig.wireframe : !!mat.wireframe;
  const metalness =
    orig?.metalness !== undefined ? orig.metalness : mat.metalness ?? 0;
  const roughness =
    orig?.roughness !== undefined ? orig.roughness : mat.roughness ?? 0.5;
  const emissiveIntensity =
    orig?.emissiveIntensity !== undefined
      ? orig.emissiveIntensity
      : mat.emissiveIntensity ?? 1;

  const backups = mat.userData?.[MAP_BACKUP_USERDATA_KEY];
  const mapBits = TEXTURE_KEYS.map((key) => {
    const live = mat[key];
    const backup = backups?.[key];
    return `${key[0]}${textureId(live || backup)}`;
  }).join('');

  return [
    mat.type || '',
    color,
    emissive,
    Math.round(emissiveIntensity * 100),
    Math.round(metalness * 100),
    Math.round(roughness * 100),
    Math.round(opacity * 100),
    Math.round((mat.transmission ?? 0) * 100),
    Math.round(alphaTest * 100),
    wireframe ? 1 : 0,
    transparent ? 1 : 0,
    // Side from Render View object setting can change live mat.side — list
    // previews always use FrontSide, so omit live side from fingerprint.
    mapBits,
  ].join('|');
}

function cacheKey(mat, fingerprint) {
  return `${mat.uuid || 'm'}::${fingerprint}`;
}

function putCache(key, dataUrl) {
  if (previewCache.has(key)) previewCache.delete(key);
  previewCache.set(key, dataUrl);
  while (previewCache.size > MAX_CACHE) {
    const oldest = previewCache.keys().next().value;
    previewCache.delete(oldest);
  }
}

function disposePreviewMaterial(mat) {
  if (!mat || mat === sharedSphere?.material) return;
  try {
    mat.dispose?.();
  } catch {
    /* ignore */
  }
}

function renderOnce(sourceMaterial) {
  ensureSharedPreviewContext();

  let previewMat;
  try {
    previewMat = sourceMaterial.clone();
  } catch {
    previewMat = new THREE.MeshStandardMaterial({
      color: sourceMaterial.color?.clone?.() || new THREE.Color(0x888888),
      metalness:
        typeof sourceMaterial.metalness === 'number'
          ? sourceMaterial.metalness
          : 0.2,
      roughness:
        typeof sourceMaterial.roughness === 'number'
          ? sourceMaterial.roughness
          : 0.6,
      opacity:
        typeof sourceMaterial.opacity === 'number'
          ? sourceMaterial.opacity
          : 1,
      transparent: !!sourceMaterial.transparent || sourceMaterial.opacity < 1,
    });
  }

  // Strip temporary Render View (Wireframe/Fade/Cutout) so the materials list
  // always shows the authored material, not the viewport display mode.
  const orig = sourceMaterial.userData?.originalMaterialProps;
  if (orig) {
    try {
      if (orig.color !== undefined && previewMat.color) {
        previewMat.color.setHex(orig.color);
      }
      if (orig.emissive !== undefined && previewMat.emissive) {
        previewMat.emissive.setHex(orig.emissive);
      }
      if (orig.emissiveIntensity !== undefined) {
        previewMat.emissiveIntensity = orig.emissiveIntensity;
      }
      if (orig.metalness !== undefined) previewMat.metalness = orig.metalness;
      if (orig.roughness !== undefined) previewMat.roughness = orig.roughness;
      if (orig.opacity !== undefined) previewMat.opacity = orig.opacity;
      if (orig.transparent !== undefined) {
        previewMat.transparent = orig.transparent;
      }
      if (orig.alphaTest !== undefined) previewMat.alphaTest = orig.alphaTest;
      if (orig.depthWrite !== undefined) previewMat.depthWrite = orig.depthWrite;
      if (orig.blending !== undefined) previewMat.blending = orig.blending;
      if (orig.wireframe !== undefined) previewMat.wireframe = orig.wireframe;
    } catch {
      /* ignore */
    }
  } else {
    // No backup: still force solid preview if live material is view-only wireframe.
    previewMat.wireframe = false;
  }

  // Keep real transparency in the list preview (only clamp near-zero so sphere stays visible).
  const srcOpacity =
    typeof previewMat.opacity === 'number' ? previewMat.opacity : 1;
  const isTransparent =
    !!previewMat.transparent ||
    srcOpacity < 0.999 ||
    !!previewMat.alphaMap ||
    (typeof previewMat.transmission === 'number' &&
      previewMat.transmission > 0);

  if (isTransparent) {
    previewMat.transparent = true;
    previewMat.opacity = Math.max(0.12, Math.min(1, srcOpacity));
    previewMat.depthWrite = false;
  } else {
    previewMat.transparent = false;
    previewMat.opacity = 1;
  }

  if (typeof sourceMaterial.transmission === 'number') {
    previewMat.transmission = sourceMaterial.transmission;
  }

  previewMat.side = THREE.FrontSide;
  previewMat.needsUpdate = true;

  const backups = sourceMaterial.userData?.[MAP_BACKUP_USERDATA_KEY];
  if (backups && typeof backups === 'object') {
    TEXTURE_KEYS.forEach((key) => {
      if (!previewMat[key] && backups[key]?.isTexture) {
        previewMat[key] = backups[key];
      }
    });
  }

  const previous = sharedSphere.material;
  sharedSphere.material = previewMat;
  sharedRenderer.setClearColor(0x505050, 1);
  sharedRenderer.clear();
  sharedRenderer.render(sharedScene, sharedCamera);
  sharedSphere.material = previous;
  disposePreviewMaterial(previewMat);

  return sharedRenderer.domElement.toDataURL('image/png');
}

/**
 * Sync path used only when cache already has a hit, or for a single immediate paint.
 * Prefer requestMaterialSpherePreview for list UIs.
 */
export function renderMaterialSpherePreview(canvas, sourceMaterial) {
  if (!canvas || !sourceMaterial) return false;
  try {
    const fp = getMaterialPreviewFingerprint(sourceMaterial);
    const key = cacheKey(sourceMaterial, fp);
    let dataUrl = previewCache.get(key);
    if (!dataUrl) {
      dataUrl = renderOnce(sourceMaterial);
      putCache(key, dataUrl);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const img = new Image();
    img.onload = () => {
      if (canvas.width !== PREVIEW_SIZE) {
        canvas.width = PREVIEW_SIZE;
        canvas.height = PREVIEW_SIZE;
      }
      ctx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
      ctx.drawImage(img, 0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    };
    img.src = dataUrl;
    return true;
  } catch (err) {
    console.warn('[MaterialSpherePreview] render failed:', err);
    return false;
  }
}

/**
 * Queue a preview render (cached). Resolves with a data URL.
 * Only one WebGL render runs at a time.
 */
export function requestMaterialSpherePreview(sourceMaterial) {
  if (!sourceMaterial) return Promise.resolve(null);

  const fp = getMaterialPreviewFingerprint(sourceMaterial);
  const key = cacheKey(sourceMaterial, fp);
  if (previewCache.has(key)) {
    return Promise.resolve(previewCache.get(key));
  }

  // Drop backlog if the list remounted / scrolled hard.
  if (queueDepth > 24) {
    return Promise.resolve(null);
  }

  queueDepth += 1;
  const job = renderQueue.then(() => {
    try {
      if (previewCache.has(key)) return previewCache.get(key);
      const dataUrl = renderOnce(sourceMaterial);
      putCache(key, dataUrl);
      return dataUrl;
    } catch (err) {
      console.warn('[MaterialSpherePreview] queued render failed:', err);
      return null;
    } finally {
      queueDepth = Math.max(0, queueDepth - 1);
    }
  });
  renderQueue = job.then(
    () => undefined,
    () => undefined
  );
  return job;
}

export function invalidateMaterialSpherePreview(material) {
  if (!material?.uuid) return;
  for (const key of [...previewCache.keys()]) {
    if (key.startsWith(`${material.uuid}::`)) previewCache.delete(key);
  }
}

export function getMaterialSpherePreviewSize() {
  return PREVIEW_SIZE;
}

export function disposeSharedMaterialSpherePreview() {
  if (!sharedRenderer) return;
  disposePreviewMaterial(sharedSphere?.material);
  sharedSphere?.geometry?.dispose?.();
  sharedBackdrop?.geometry?.dispose?.();
  sharedBackdrop?.material?.dispose?.();
  sharedRenderer.dispose();
  sharedRenderer = null;
  sharedScene = null;
  sharedCamera = null;
  sharedSphere = null;
  sharedBackdrop = null;
  previewCache.clear();
  renderQueue = Promise.resolve();
  queueDepth = 0;
}
